'use client';

import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { useState } from 'react';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');


  const GOOGLE_SHEET_URL = 'https://script.google.com/macros/s/AKfycbwJ6KEyYiVeJ_eQl1KyYmg1ziq4mgA5GofEZlK50kTovPg_vdaKQ4gleVFUI_yvdfbLiA/exec';

  const handleSubscribe = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!email) return;

  setLoading(true);
  setErrorMsg('');
  setSuccess(false);

  try {
    const formData = new FormData();
    formData.append('email', email);
    
    const res = await fetch(GOOGLE_SHEET_URL, {
      method: 'POST',
      body: formData,
    });

    const data = await res.json();

    if (data.status === 'duplicate') {
      setErrorMsg('⚠️ This email is already subscribed.');
      return;
    }

    if (data.status === 'success') {
      setSuccess(true);
      setEmail('');
      return;
    }

    setErrorMsg('❌ Something went wrong.');

  } catch (err) {
    setErrorMsg('❌ Network error. Please try later.');
  } finally {
    setLoading(false);
  }
};




  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'About Us', href: '/about-us' },
    { label: 'Destinations', href: '/destinations' },
    //{ label: 'Services', href: '/services-overview' },
    { label: 'Book Consultation', href: '/consultation-booking' },
    { label: 'Contact', href: '/contact' },
    { label: 'Blog', href: '/blog' },
    { label: 'Privacy Policy', href: '/privacy' },
    //{ label: 'Blog Hub', href: '/blog-list-page' }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'facebook', href: 'https://www.facebook.com/profile.php?id=61584906437305#' },
    { name: 'Twitter', icon: 'twitter', href: 'https://x.com/Yunovaintl' },
    { name: 'LinkedIn', icon: 'linkedin', href: 'https://www.linkedin.com/company/yunovainternational' },
    { name: 'Instagram', icon: 'instagram', href: 'https://www.instagram.com/yunovainternational/' },
    { name: 'Whatsapp', icon: 'whatsapp', href: 'https://wa.me/919265383750?text=Hi%20Yunova' }
  ];

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <Link href="/" className="inline-block mb-4">
              <Image 
                src="/assets/images/Yunova_International_Logo_Design_-_Original-1766728974344.png"
                alt="Yunova International Logo"
                width={150}
                height={40}
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-muted-foreground text-sm mb-4">
              Your trusted partner in international education, helping students achieve their dreams of studying abroad.
            </p>
            <div className="flex space-x-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors duration-smooth"
                  aria-label={social?.name}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-all duration-smooth">
                    {social?.icon === 'facebook' && (
                      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    )}
                    {social?.icon === 'twitter' && (
                      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    )}
                    {social?.icon === 'linkedin' && (
                      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    )}
                    {social?.icon === 'instagram' && (
                      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                      </svg>
                    )}
                    {social?.icon === 'whatsapp' && (
                       <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.52 3.48A11.86 11.86 0 0 0 12.03 0C5.39 0 .01 5.38.01 12.02c0 2.12.55 4.19 1.6 6.03L0 24l6.15-1.61a12.01 12.01 0 0 0 5.88 1.5h.01c6.63 0 12.01-5.38 12.01-12.02 0-3.2-1.25-6.21-3.53-8.39zM12.04 21.3a9.3 9.3 0 0 1-4.74-1.29l-.34-.2-3.65.96.97-3.56-.22-.36a9.28 9.28 0 1 1 7.98 4.45zm5.11-6.98c-.28-.14-1.65-.81-1.9-.9-.25-.09-.43-.14-.61.14-.18.28-.7.9-.86 1.09-.16.18-.32.2-.6.07-.28-.14-1.17-.43-2.23-1.37-.83-.74-1.39-1.66-1.55-1.94-.16-.28-.02-.43.12-.57.12-.12.28-.32.42-.48.14-.16.18-.28.28-.46.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.01-.22-.54-.45-.47-.61-.47h-.52c-.18 0-.48.07-.73.34-.25.28-.96.94-.96 2.29 0 1.35.99 2.66 1.13 2.84.14.18 1.95 2.98 4.73 4.18.66.29 1.18.46 1.58.59.66.21 1.26.18 1.73.11.53-.08 1.65-.68 1.88-1.34.23-.66.23-1.23.16-1.34-.07-.11-.25-.18-.53-.32z"/>
                      </svg>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-foreground font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="text-muted-foreground hover:text-primary transition-colors duration-smooth text-sm"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-foreground font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start space-x-2">
                <Icon name="MapPinIcon" size={16} className="mt-1 flex-shrink-0 text-primary" />
                <span className="cursor-pointer hover:text-primary"><a href="https://maps.app.goo.gl/CcKXLtbE2zvNWsSH8">203, 2nd Floor, Sun Orbit, Rajpath Rangoli Road, near Pandit Dindayal Hall, Bodakdev, Ahmedabad, Gujarat 380059</a></span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="PhoneIcon" size={16} className="flex-shrink-0 text-secondary" />
                <span className="cursor-pointer hover:text-primary"><a href="tel:+917966826611">+91 79668 26611</a></span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="EnvelopeIcon" size={16} className="flex-shrink-0 text-accent" />
                <span className="cursor-pointer hover:text-primary"><a href="mailto:info@yunovainternational.com">info@yunovainternational.com</a></span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-foreground font-semibold mb-4">Newsletter</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Get exclusive updates on new scholarships, university partnerships, application deadlines, 
            and success stories delivered straight to your inbox.
            </p>
            {/*<form className="flex flex-col space-y-2">
               <input
                 type="email"
                 placeholder="Your email"
                 className="px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
               />
               <button
                 type="submit"
                 className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors duration-smooth text-sm font-medium"
               >
                 Subscribe
               </button>
             </form>*/}
            <form onSubmit={handleSubscribe} className="flex flex-col space-y-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />

              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors duration-smooth text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>

              {success && (
                <p className="text-green-600 text-xs mt-1">
                  ✅ Thanks for subscribing!
                </p>
              )}
              {errorMsg && (
                <p className="text-red-600 text-xs mt-1">
                  {errorMsg}
                </p>
              )}

            </form>
          </div>
        </div>

        <div className="border-t border-border mt-8 pt-8 text-center">
          <p className="text-muted-foreground text-sm">
            © {currentYear} Yunova International Private Limited. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;